/**
 * Face-edge kernel — OFFSET and FOLLOW ME (2026-09-27, Matt: "Add an offset tool that
 * works just like SketchUp … Also, I want a follow me tool that allows for a profile to
 * be extruded along a path, just like SketchUp").
 *
 * SketchUp's documented behavior this follows (help.sketchup.com — "Offsetting a Line
 * from Existing Geometry", "Extruding with Follow Me"):
 *  - OFFSET works on a face (its whole perimeter — outer loop AND holes, so a face
 *    offsets "into itself") or on two or more CONNECTED, COPLANAR edges. The new edges
 *    are real geometry (a closed offset inside a face subdivides it). Corners miter.
 *    SketchUp removes overlaps an offset creates by default; v1 REFUSES an offset
 *    whose result would cross itself instead (recorded) — never a tangled loop.
 *  - FOLLOW ME extrudes a profile FACE along a PATH (a continuous edge chain, or a
 *    closed loop — a circle lathes). The profile only needs to be roughly
 *    perpendicular to the path, not touching it. The profile travels along the path by
 *    exact miter projection: at each path corner it is projected (along the incoming
 *    segment) onto the bisector plane, so every side is a flat trapezoid and joints
 *    are clean miters. An open path keeps the profile as the start cap and heals an
 *    end cap; a closed path joins back to itself (no caps; the original profile face,
 *    now interior, is removed).
 *
 * Both run on a scratch copy, insert through the arrangement (so results STICK to and
 * subdivide what they touch) and must pass the validity gate; a refusal changes nothing.
 */

import type { BodyTopology, BodyPt3, BodyEid, BodyFid, BodyVid } from './types'
import { WELD_TOL, frameForPlane, to2, from2, planeFromPoints, add, sub, scale, dot, norm, len } from './geom3'
import type { Pt2 } from './geom3'
import { loopPoints, invariants, facesUsingEdge, gcOrphanVertices } from './topo'
import { insertSegment } from './arrange'
import { edgePiercesFace } from './extrude'

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T
export type OFResult = { ok: true } | { ok: false; error: string }

function commit(topo: BodyTopology, work: BodyTopology): OFResult {
  if (invariants(work).length || edgePiercesFace(work)) return { ok: false, error: 'That would cross existing geometry — not applied.' }
  topo.vertices = work.vertices
  topo.edges = work.edges
  topo.faces = work.faces
  topo.curves = work.curves
  return { ok: true }
}

// ---------------------------------------------------------------------------
// OFFSET
// ---------------------------------------------------------------------------

/** miter-offset a 2D polyline by d along each segment's LEFT normal */
export function offsetPolyline2(pts: Pt2[], closed: boolean, d: number, bevel = false): Pt2[] | null {
  const n = pts.length
  if (n < 2) return null
  const dir = (a: Pt2, b: Pt2) => { const dx = b.x - a.x, dy = b.y - a.y, l = Math.hypot(dx, dy); return l > 1e-12 ? { x: dx / l, y: dy / l } : null }
  const left = (v: { x: number; y: number }) => ({ x: -v.y, y: v.x })
  const out: Pt2[] = []
  for (let i = 0; i < n; i++) {
    const prev = closed ? pts[(i - 1 + n) % n] : (i > 0 ? pts[i - 1] : null)
    const next = closed ? pts[(i + 1) % n] : (i < n - 1 ? pts[i + 1] : null)
    const da = prev ? dir(prev, pts[i]) : null
    const db = next ? dir(pts[i], next) : null
    if (!da && !db) return null
    const na = da ? left(da) : null, nb = db ? left(db) : null
    let m: Pt2
    if (na && nb) {
      const mx = na.x + nb.x, my = na.y + nb.y, ml = Math.hypot(mx, my)
      const u = ml < 1e-9 ? null : { x: mx / ml, y: my / ml }
      const c = u ? u.x * na.x + u.y * na.y : 0
      if (!u || c < (bevel ? 0.25 : 0.1)) {
        if (!bevel) return null                              // a 180° fold / needle-sharp corner
        // BEVEL: the two offset ends at distance d — the overlap cleanup trims what folds
        out.push({ x: pts[i].x + na.x * d, y: pts[i].y + na.y * d })
        out.push({ x: pts[i].x + nb.x * d, y: pts[i].y + nb.y * d })
        continue
      }
      m = { x: u.x * d / c, y: u.y * d / c }
    } else {
      const nn = (na || nb)!
      m = { x: nn.x * d, y: nn.y * d }
    }
    out.push({ x: pts[i].x + m.x, y: pts[i].y + m.y })
  }
  return out
}

/** does a 2D polyline cross itself (non-adjacent segments intersecting)? */
function selfCrosses(p: Pt2[], closed: boolean): boolean {
  const segs: [Pt2, Pt2][] = []
  for (let i = 0; i + 1 < p.length; i++) segs.push([p[i], p[i + 1]])
  if (closed) segs.push([p[p.length - 1], p[0]])
  const cr = (a: Pt2, b: Pt2, c: Pt2) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
  for (let i = 0; i < segs.length; i++) {
    for (let j = i + 1; j < segs.length; j++) {
      if (j === i + 1 || (closed && i === 0 && j === segs.length - 1)) continue
      const [a, b] = segs[i], [c, e] = segs[j]
      const d1 = cr(a, b, c), d2 = cr(a, b, e), d3 = cr(c, e, a), d4 = cr(c, e, b)
      if (((d1 > 1e-12 && d2 < -1e-12) || (d1 < -1e-12 && d2 > 1e-12)) && ((d3 > 1e-12 && d4 < -1e-12) || (d3 < -1e-12 && d4 > 1e-12))) return true
    }
  }
  return false
}

/**
 * The offset rings of a face (every loop) — d > 0 goes INTO the face (the outer loop
 * shrinks, holes grow), d < 0 goes out. 3D polylines, closed. null = impossible.
 */
export function faceOffsetRings(topo: BodyTopology, fid: BodyFid, d: number): BodyPt3[][] | null {
  const F = topo.faces[fid]
  if (!F) return null
  const frame = frameForPlane(F.plane)
  const out: BodyPt3[][] = []
  for (const loop of F.loops) {
    const p2 = loopPoints(topo, loop).map((q) => to2(frame, q))
    // loops are oriented (outer CCW, holes CW about n) — the LEFT normal always points
    // into the face, so one sign serves every loop
    const off = offsetPolyline2(p2, true, d)
    if (!off || selfCrosses(off, true)) return null
    // an inward offset past the middle turns the loop inside out (area flips sign or
    // vanishes) — impossible, not a smaller loop
    const area = (q: Pt2[]) => q.reduce((a, pt, i) => { const r = q[(i + 1) % q.length]; return a + pt.x * r.y - r.x * pt.y }, 0) / 2
    const a0 = area(p2), a1 = area(off)
    if (Math.sign(a0) !== Math.sign(a1) || Math.abs(a1) < WELD_TOL) return null
    out.push(off.map((q) => from2(frame, q)))
  }
  return out
}

// ---------------------------------------------------------------------------
// OVERLAP CLEANUP (2026-09-28, Matt: "I think sketchup allows certain lines to overlap,
// but our version doesn't?"). SketchUp's Offset trims the parts of an offset that fold
// over themselves (a U offset inward, a notched outline, a hole growing into the edge)
// and keeps the clean outline; with Alt held it keeps every line, crossings included.
// The rule: split the raw offset at every crossing, then keep a piece only if it is a
// TRUE offset — the full distance away from the original outline, on the right side.
// A folded-over piece is always closer than that to some other part of the outline.
// ---------------------------------------------------------------------------

type Seg2 = [Pt2, Pt2]
const segsOf = (p: Pt2[], closed: boolean): Seg2[] => {
  const out: Seg2[] = []
  for (let i = 0; i + 1 < p.length; i++) out.push([p[i], p[i + 1]])
  if (closed && p.length > 2) out.push([p[p.length - 1], p[0]])
  return out
}
function distPtSeg(p: Pt2, a: Pt2, b: Pt2): number {
  const ex = b.x - a.x, ey = b.y - a.y, l2 = ex * ex + ey * ey
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * ex + (p.y - a.y) * ey) / l2)) : 0
  return Math.hypot(p.x - (a.x + ex * t), p.y - (a.y + ey * t))
}
/** every segment split at every proper crossing with every other */
function splitAtCrossings(segs: Seg2[]): Seg2[] {
  const ts: number[][] = segs.map(() => [0, 1])
  for (let i = 0; i < segs.length; i++) {
    const [a, b] = segs[i]
    for (let j = i + 1; j < segs.length; j++) {
      const [c, e] = segs[j]
      const rx = b.x - a.x, ry = b.y - a.y, sx = e.x - c.x, sy = e.y - c.y
      const den = rx * sy - ry * sx
      if (Math.abs(den) < 1e-14) continue
      const qx = c.x - a.x, qy = c.y - a.y
      const t = (qx * sy - qy * sx) / den
      const u = (qx * ry - qy * rx) / den
      if (t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) ts[i].push(t)
      if (u > 1e-9 && u < 1 - 1e-9 && t >= -1e-9 && t <= 1 + 1e-9) ts[j].push(u)
    }
  }
  const out: Seg2[] = []
  segs.forEach(([a, b], i) => {
    const list = [...new Set(ts[i])].sort((x, y) => x - y)
    for (let k = 0; k + 1 < list.length; k++) {
      const p = { x: a.x + (b.x - a.x) * list[k], y: a.y + (b.y - a.y) * list[k] }
      const q = { x: a.x + (b.x - a.x) * list[k + 1], y: a.y + (b.y - a.y) * list[k + 1] }
      if (Math.hypot(q.x - p.x, q.y - p.y) > WELD_TOL) out.push([p, q])
    }
  })
  return out
}
/** even-odd: is p inside the region bounded by these rings (outer + holes)? */
function insideRings(p: Pt2, rings: Pt2[][]): boolean {
  let inside = false
  for (const r of rings) {
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const a = r[i], b = r[j]
      if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside
    }
  }
  return inside
}
/** the raw offset pieces → what Offset actually draws */
function cleanOffset(raw: Seg2[], boundary: Seg2[], dist: number, side: ((p: Pt2) => boolean) | null, keepOverlaps: boolean): Seg2[] {
  const pieces = splitAtCrossings(raw)
  if (keepOverlaps) return pieces
  const tol = Math.max(WELD_TOL, dist * 1e-6)
  return pieces.filter(([a, b]) => {
    const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    for (const [c, e] of boundary) if (distPtSeg(m, c, e) < dist - tol) return false
    return side ? side(m) : true
  })
}

/**
 * Offset a face's perimeter (every loop) by d — d > 0 INTO the face, d < 0 out — the
 * SketchUp way: pieces that fold over are trimmed (keepOverlaps = Alt keeps them all).
 * -> 3D segments | null (nothing left, e.g. offset in past the middle)
 */
export function faceOffsetSegments(topo: BodyTopology, fid: BodyFid, d: number, keepOverlaps = false): [BodyPt3, BodyPt3][] | null {
  const F = topo.faces[fid]
  if (!F || !(Math.abs(d) > WELD_TOL)) return null
  const frame = frameForPlane(F.plane)
  const rings = F.loops.map((loop) => loopPoints(topo, loop).map((q) => to2(frame, q)))
  const raw: Seg2[] = []
  for (const r of rings) {
    // loops are oriented (outer CCW, holes CW about n): the LEFT normal points into the face
    const off = offsetPolyline2(r, true, d, true)
    if (off) raw.push(...segsOf(off, true))
  }
  const boundary = rings.flatMap((r) => segsOf(r, true))
  const into = d > 0
  const kept = cleanOffset(raw, boundary, Math.abs(d), (m) => insideRings(m, rings) === into, keepOverlaps)
  return kept.length ? kept.map(([a, b]) => [from2(frame, a), from2(frame, b)]) : null
}

/** Offset an edge chain the same way (the side the cursor is on) -> 3D segments | null */
export function chainOffsetSegments(topo: BodyTopology, eids: BodyEid[], d: number, towardPt: BodyPt3, keepOverlaps = false): [BodyPt3, BodyPt3][] | null {
  const ch = orderChain(topo, eids)
  if (!ch || ch.vids.length < 2 || !(Math.abs(d) > WELD_TOL)) return null
  const pts3 = ch.vids.map((v) => topo.vertices[v])
  const pl = pts3.length >= 3 ? planeFromPoints(pts3) : null
  if (!pl) return null
  for (const q of pts3) if (Math.abs(dot(pl.n, q) - pl.d) > WELD_TOL * 4) return null   // not coplanar
  const frame = frameForPlane(pl)
  const p2 = pts3.map((q) => to2(frame, q))
  const t2 = to2(frame, towardPt)
  let best = Infinity, sideSign = 1
  const segN = ch.closed ? p2.length : p2.length - 1
  for (let i = 0; i < segN; i++) {
    const a = p2[i], b = p2[(i + 1) % p2.length]
    const dd = distPtSeg(t2, a, b)
    if (dd < best) { best = dd; sideSign = (b.x - a.x) * (t2.y - a.y) - (b.y - a.y) * (t2.x - a.x) >= 0 ? 1 : -1 }
  }
  const off = offsetPolyline2(p2, ch.closed, Math.abs(d) * sideSign, true)
  if (!off) return null
  const boundary = segsOf(p2, ch.closed)
  // a closed chain has a real inside/outside; an open one only its distance
  const side = ch.closed ? ((m: Pt2) => insideRings(m, [p2]) === insideRings(t2, [p2])) : null
  const kept = cleanOffset(segsOf(off, ch.closed), boundary, Math.abs(d), side, keepOverlaps)
  return kept.length ? kept.map(([a, b]) => [from2(frame, a), from2(frame, b)]) : null
}

/** insert offset segments into the topology (validity-gated) */
export function insertSegments(topo: BodyTopology, segs: [BodyPt3, BodyPt3][]): OFResult {
  const work = clone(topo)
  for (const [a, b] of segs) insertSegment(work, a, b)
  return commit(topo, work)
}

/** order a set of edges into ONE chain -> { vids (in order), closed } | null (branching / disconnected) */
export function orderChain(topo: BodyTopology, eids: BodyEid[]): { vids: BodyVid[]; closed: boolean } | null {
  const es = eids.filter((e) => topo.edges[e])
  if (!es.length) return null
  const adj = new Map<BodyVid, BodyEid[]>()
  for (const e of es) {
    const { a, b } = topo.edges[e]
    if (!adj.has(a)) adj.set(a, [])
    if (!adj.has(b)) adj.set(b, [])
    adj.get(a)!.push(e); adj.get(b)!.push(e)
  }
  if ([...adj.values()].some((l) => l.length > 2)) return null
  const ends = [...adj.entries()].filter(([, l]) => l.length === 1).map(([v]) => v)
  if (ends.length !== 0 && ends.length !== 2) return null
  const closed = ends.length === 0
  const start = closed ? topo.edges[es[0]].a : ends[0]
  const vids: BodyVid[] = [start]
  const used = new Set<BodyEid>()
  let cur = start
  for (;;) {
    const nextE = adj.get(cur)!.find((e) => !used.has(e))
    if (!nextE) break
    used.add(nextE)
    const e = topo.edges[nextE]
    cur = e.a === cur ? e.b : e.a
    if (closed && cur === start) break
    vids.push(cur)
  }
  if (used.size !== es.length) return null
  return { vids, closed }
}

/**
 * The offset of a coplanar edge CHAIN by d on the side of `towardPt` (a 3D point in
 * the chain's plane). -> { pts, closed } | null
 */
export function chainOffset(topo: BodyTopology, eids: BodyEid[], d: number, towardPt: BodyPt3): { pts: BodyPt3[]; closed: boolean } | null {
  const ch = orderChain(topo, eids)
  if (!ch || ch.vids.length < 2) return null
  const pts3 = ch.vids.map((v) => topo.vertices[v])
  const pl = pts3.length >= 3 ? planeFromPoints(pts3) : null
  if (!pl) return null
  for (const q of pts3) if (Math.abs(dot(pl.n, q) - pl.d) > WELD_TOL * 4) return null   // not coplanar
  const frame = frameForPlane(pl)
  const p2 = pts3.map((q) => to2(frame, q))
  // which side is the cursor on: the left side of the nearest segment → +d
  const t2 = to2(frame, towardPt)
  let best = Infinity, side = 1
  const segN = ch.closed ? p2.length : p2.length - 1
  for (let i = 0; i < segN; i++) {
    const a = p2[i], b = p2[(i + 1) % p2.length]
    const ex = b.x - a.x, ey = b.y - a.y, l2 = ex * ex + ey * ey
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((t2.x - a.x) * ex + (t2.y - a.y) * ey) / l2)) : 0
    const dd = Math.hypot(t2.x - (a.x + ex * t), t2.y - (a.y + ey * t))
    if (dd < best) { best = dd; side = ex * (t2.y - a.y) - ey * (t2.x - a.x) >= 0 ? 1 : -1 }
  }
  const off = offsetPolyline2(p2, ch.closed, d * side)
  if (!off || selfCrosses(off, ch.closed)) return null
  return { pts: off.map((q) => from2(frame, q)), closed: ch.closed }
}

/** insert offset polylines into the topology (validity-gated) */
export function insertPolylines(topo: BodyTopology, lines: { pts: BodyPt3[]; closed: boolean }[]): OFResult {
  const work = clone(topo)
  for (const l of lines) {
    const n = l.pts.length
    const m = l.closed ? n : n - 1
    for (let i = 0; i < m; i++) insertSegment(work, l.pts[i], l.pts[(i + 1) % n])
  }
  return commit(topo, work)
}

// ---------------------------------------------------------------------------
// FOLLOW ME
// ---------------------------------------------------------------------------

/**
 * Sweep face `profileFid` along the path polyline `path` (3D points, in order;
 * `closed` joins the last point back to the first).
 */
export function followMe(topo: BodyTopology, profileFid: BodyFid, pathIn: BodyPt3[], closed: boolean): OFResult {
  const F = topo.faces[profileFid]
  if (!F) return { ok: false, error: 'Click the profile FACE.' }
  if (pathIn.length < 2) return { ok: false, error: 'The path needs at least one edge.' }
  const rings = F.loops.map((l) => loopPoints(topo, l).map((q) => ({ ...q })))
  const centroid = scale(rings[0].reduce((s, q) => add(s, q), { x: 0, y: 0, z: 0 }), 1 / rings[0].length)
  // start the path at the end nearest the profile (open paths); a closed path starts
  // at its vertex nearest the profile
  let path = pathIn.map((q) => ({ ...q }))
  if (!closed) {
    const d0 = len(sub(path[0], centroid)), d1 = len(sub(path[path.length - 1], centroid))
    if (d1 < d0) path = path.reverse()
  } else {
    let bi = 0, bd = Infinity
    path.forEach((q, i) => { const dd = len(sub(q, centroid)); if (dd < bd) { bd = dd; bi = i } })
    path = [...path.slice(bi), ...path.slice(0, bi)]
  }
  const n = path.length
  const segs = closed ? n : n - 1
  const dirs: BodyPt3[] = []
  for (let i = 0; i < segs; i++) {
    const d = sub(path[(i + 1) % n], path[i])
    if (len(d) < WELD_TOL) return { ok: false, error: 'The path has a zero-length edge.' }
    dirs.push(norm(d))
  }
  const pn = norm(F.plane.n)
  if (Math.abs(dot(pn, dirs[0])) < 0.2) return { ok: false, error: 'The profile must face along the path (draw it roughly perpendicular to the first path edge).' }
  /** project each point of `ring` along `d` onto the plane through `o` with normal `m` */
  const project = (ring: BodyPt3[], d: BodyPt3, o: BodyPt3, m: BodyPt3): BodyPt3[] | null => {
    const den = dot(m, d)
    if (Math.abs(den) < 1e-9) return null
    return ring.map((q) => add(q, scale(d, dot(m, sub(o, q)) / den)))
  }
  const miter = (i: number) => norm(add(dirs[(i - 1 + segs) % segs], dirs[i % segs]))
  // sections[i][ring] — the profile's rings at each path vertex
  const sections: BodyPt3[][][] = []
  let first = rings
  if (closed) {
    const s0 = rings.map((r) => project(r, dirs[0], path[0], miter(0)))
    if (s0.some((r) => !r)) return { ok: false, error: 'The path turns too sharply here.' }
    first = s0 as BodyPt3[][]
  }
  sections.push(first)
  for (let i = 1; i < n; i++) {
    const m = (!closed && i === n - 1) ? dirs[segs - 1] : miter(i)
    const next = sections[i - 1].map((r) => project(r, dirs[i - 1], path[i], m))
    if (next.some((r) => !r)) return { ok: false, error: 'The path turns too sharply here.' }
    sections.push(next as BodyPt3[][])
  }
  if (closed) sections.push(sections[0])   // join back exactly
  const work = clone(topo)
  // rings at every section, then the longitudinal edges; planar loops heal into faces
  for (const sec of sections) for (const r of sec) for (let k = 0; k < r.length; k++) insertSegment(work, r[k], r[(k + 1) % r.length])
  for (let i = 0; i + 1 < sections.length; i++) {
    for (let j = 0; j < sections[i].length; j++) {
      for (let k = 0; k < sections[i][j].length; k++) insertSegment(work, sections[i][j][k], sections[i + 1][j][k])
    }
  }
  // interior miter sections healed into faces — they're INSIDE the swept solid: remove
  const near = (a: BodyPt3, b: BodyPt3) => len(sub(a, b)) <= WELD_TOL * 4
  const interior = closed ? sections.slice(0, -1) : sections.slice(1, -1)
  for (const sec of interior) {
    const pts = sec.flat()
    for (const [fid, f] of Object.entries(work.faces)) {
      const vs = f.loops.flatMap((l) => loopPoints(work, l))
      if (vs.every((v) => pts.some((q) => near(q, v)))) delete work.faces[fid]
    }
  }
  if (closed) {
    // a closed path makes every profile vertex trace a closed, planar track — the
    // healer fills such a loop with a face (a skin across the frame's hole). A face
    // made only of ONE vertex's track is never part of a sweep: remove it
    for (let j = 0; j < sections[0].length; j++) {
      for (let k = 0; k < sections[0][j].length; k++) {
        const track = sections.map((sec) => sec[j][k])
        for (const [fid, f] of Object.entries(work.faces)) {
          const vs = f.loops.flatMap((l) => loopPoints(work, l))
          // on the track POLYLINE (a profile corner can sit mid-track, splitting it)
          const onTrack = (v: BodyPt3) => track.some((q, i) => {
            const r = track[(i + 1) % track.length]
            const ab = sub(r, q), l2 = dot(ab, ab)
            const tt = l2 > 0 ? Math.max(0, Math.min(1, dot(sub(v, q), ab) / l2)) : 0
            return len(sub(v, add(q, scale(ab, tt)))) <= WELD_TOL * 4
          })
          if (vs.length >= 3 && vs.every(onTrack)) delete work.faces[fid]
        }
      }
    }
    // the original profile is now inside the lathe — its face goes, and its edges
    // unless something still uses them
    const pv = new Set<string>()
    for (const l of F.loops) for (const eid of l.edges) { pv.add(eid) }
    delete work.faces[profileFid]
    for (const eid of pv) if (work.edges[eid] && !facesUsingEdge(work, eid).length) delete work.edges[eid]
    gcOrphanVertices(work)
  }
  return commit(topo, work)
}
